import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchActivity, type AccountView, type ActivityView } from '../../api/admin';
import known from '../../generated/activity-kinds.json';
import { ca } from '../../i18n/ca';
import { kindGroups, kindLabel } from '../../i18n/ca-activity';
import { activityLine, actorName } from './activitySentence';
import { errorText, fmtClock, fmtDayLong } from './shared';

/** The log cut into the days a person reads it by, newest day first, as the platform sent it. */
function byDay(activity: ActivityView[]): [string, ActivityView[]][] {
  const days = new Map<string, ActivityView[]>();
  for (const entry of activity) {
    const key = fmtDayLong(entry.occurredAt);
    days.set(key, [...(days.get(key) ?? []), entry]);
  }
  return [...days.entries()];
}

/**
 * Activity tab: the platform's log, newest first, every entry one Catalan sentence about what
 * somebody did («Marta ha desactivat el compte de Pau»), never a key. The sentence is the
 * platform's own, shared with the profile's history, and `activitySentence.ts` puts the account's
 * name in front of it; a kind nobody wrote a sentence for reads as a neutral line with its key in
 * small print, so an event is never lost. The log is filtered by account and by kind on the
 * platform, in parts an admin asks the next of, and both filters live in the URL
 * (`?tab=activity&account=2&kind=role.granted`) so a link shares what was looked at.
 */
export function ActivityTab({
  accounts,
  onError,
}: {
  accounts: AccountView[];
  onError: (message: string | null) => void;
}) {
  const [search, setSearch] = useSearchParams();
  const accountParam = Number(search.get('account'));
  const account = Number.isInteger(accountParam) && accountParam > 0 ? accountParam : undefined;
  const kind = search.get('kind') || undefined;

  const [entries, setEntries] = useState<ActivityView[]>([]);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(true);
  // Which filters the entries on screen answer: an older part asked for under other filters is dropped.
  const generation = useRef(0);
  const who = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);

  // The first part, again whenever the filters change; an answer to an older filter is dropped.
  useEffect(() => {
    let current = true;
    generation.current += 1;
    setLoading(true);
    fetchActivity({ account, kind })
      .then((page) => {
        if (!current) return;
        setEntries(page.entries);
        setMore(page.more);
        onError(null);
      })
      .catch((err) => current && onError(errorText(err)))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [account, kind, onError]);

  const older = useCallback(async () => {
    const last = entries[entries.length - 1];
    if (!last) return;
    const asked = generation.current;
    setLoading(true);
    try {
      const page = await fetchActivity({ account, kind, before: last.id });
      if (asked !== generation.current) return;
      setEntries((list) => [...list, ...page.entries]);
      setMore(page.more);
    } catch (err) {
      if (asked === generation.current) onError(errorText(err));
    } finally {
      if (asked === generation.current) setLoading(false);
    }
  }, [entries, account, kind, onError]);

  function setFilter(name: 'account' | 'kind', value: string) {
    const next = new URLSearchParams(search);
    if (value) next.set(name, value);
    else next.delete(name);
    setSearch(next, { replace: true });
  }

  const groups = useMemo(() => {
    const kinds = kind && !known.kinds.includes(kind) ? [...known.kinds, kind] : known.kinds;
    return kindGroups(kinds);
  }, [kind]);
  const people = useMemo(
    () => [...accounts].sort((a, b) => actorName(a).localeCompare(actorName(b), 'ca')),
    [accounts],
  );
  const f = ca.adminUi.activityFilters;

  return (
    <section className="stack" aria-label={ca.activity}>
      <p className="muted small">{ca.adminUi.intros.activity}</p>
      <div className="row activity-filters">
        <label className="field">
          <span>{f.account}</span>
          <select value={account ?? ''} onChange={(e) => setFilter('account', e.target.value)}>
            <option value="">{f.allAccounts}</option>
            {people.map((a) => (
              <option key={a.id} value={a.id}>
                {actorName(a)} (@{a.handle})
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{f.kind}</span>
          <select value={kind ?? ''} onChange={(e) => setFilter('kind', e.target.value)}>
            <option value="">{f.allKinds}</option>
            {groups.map((g) => (
              <optgroup key={g.area} label={g.label}>
                {g.kinds.map((k) => (
                  <option key={k} value={k}>
                    {kindLabel(k) ?? `${ca.adminUi.act.unknownKind} (${k})`}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        {(account != null || kind) && (
          <button
            type="button"
            className="ghost"
            onClick={() => setSearch({ tab: 'activity' }, { replace: true })}
          >
            <i className="bi bi-x-lg" aria-hidden="true" /> {f.clear}
          </button>
        )}
      </div>
      {account != null && <p className="muted small">{f.aboutAccount}</p>}
      {entries.length === 0 ? (
        <div className="empty">
          <i className="bi bi-clock-history" aria-hidden="true" />
          <p>{loading ? f.loading : account != null || kind ? f.none : ca.adminUi.noActivity}</p>
        </div>
      ) : (
        <>
          {byDay(entries).map(([day, list]) => (
            <Fragment key={day}>
              <h3 className="section">{day}</h3>
              <ul className="list activity-log">
                {list.map((e) => {
                  const line = activityLine(e, who);
                  return (
                    <li key={e.id}>
                      <span className="muted small when">{fmtClock(e.occurredAt)}</span>
                      {line.actor ? (
                        <Link
                          className="actor"
                          to={`/u/${line.actor.handle}`}
                          title={actorName(line.actor)}
                        >
                          {actorName(line.actor)}
                        </Link>
                      ) : line.lead ? (
                        <span className="actor muted">{line.lead}</span>
                      ) : null}
                      <span className="grow">
                        {line.text}
                        {line.unknown && <span className="muted small kind"> {e.kind}</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Fragment>
          ))}
          <div className="row">
            <span className="muted small">{f.shown(entries.length)}</span>
            {more && (
              <button type="button" disabled={loading} onClick={() => void older()}>
                <i className="bi bi-arrow-down-short" aria-hidden="true" /> {f.older}
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
